import * as THREE from 'three'
import Materials, { MaterialType } from './mesh/materials'
import Block from './mesh/block'
import Highlight from './highlight'
import Noise from './noise'

import Generate from './worker/generate?worker'

export enum BlockType {
  grass = 0,
  sand = 1,
  tree = 2,
  leaf = 3,
  dirt = 4,
  stone = 5,
  coal = 6,
  wood = 7,
  diamond = 8,
  quartz = 9,
  glass = 10,
  bedrock = 11,
  sandstone = 12,
  cactus = 13,
  jungleTree = 14,
  jungleLeaf = 15,
  jungleWood = 16,
  water = 17,
  ironOre = 18,
  diamondOre = 19,
  furnace = 20,
  chest = 21
}

export enum BiomeType {
  plains = 'plains',
  desert = 'desert',
  jungle = 'jungle'
}

export const getBiomeType = (x: number, z: number): BiomeType => {
  const angle = Math.atan2(z, x)

  if (angle > Math.PI / 4 && angle <= (Math.PI * 3) / 4) {
    return BiomeType.jungle
  }
  if (angle < -Math.PI / 4 && angle >= (-Math.PI * 3) / 4) {
    return BiomeType.desert
  }
  return BiomeType.plains
}

export const getSurfaceBlockType = (
  x: number,
  z: number,
  yOffset: number,
  stoneOffset: number,
  coalOffset: number,
  noise: Noise
): BlockType => {
  if (stoneOffset > noise.stoneThreshold) {
    return coalOffset > noise.coalThreshold ? BlockType.coal : BlockType.stone
  }

  const biome = getBiomeType(x, z)

  if (biome === BiomeType.desert) {
    return yOffset < -5 ? BlockType.sandstone : BlockType.sand
  }

  if (yOffset < -3) {
    return BlockType.sand
  }

  return BlockType.grass
}

const isCaveAir = (x: number, y: number, z: number, noise: Noise) => {
  if (y <= 3 || y > 27) {
    return false
  }
  const tunnel = Math.abs(noise.get(x / 12, y / 8, noise.stoneSeed + z / 12))
  const chamber = noise.get((x + 90) / 22, y / 14, noise.coalSeed + z / 22)
  return tunnel < 0.09 || chamber > 0.52
}

const getOreType = (x: number, y: number, z: number, noise: Noise) => {
  const diamondNoise =
    noise.get((x - 30) / 4, (z + 55) / 4, noise.coalSeed + y / 5) * 8
  const ironNoise =
    noise.get((x + 55) / 5, (z - 35) / 5, noise.stoneSeed + y / 6) * 8
  const coalNoise =
    noise.get(x / noise.coalGap, z / noise.coalGap, noise.coalSeed + y / 10) *
    noise.coalAmp

  if (y < 12 && diamondNoise > 5.25) {
    return BlockType.diamondOre
  }
  if (y < 24 && ironNoise > 4.85) {
    return BlockType.ironOre
  }
  if (coalNoise > noise.coalThreshold) {
    return BlockType.coal
  }
  return BlockType.stone
}

export default class Terrain {
  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera) {
    this.scene = scene
    this.camera = camera
    this.maxCount =
      (this.distance * this.chunkSize * 2 + this.chunkSize) ** 2 + 500
    this.highlight = new Highlight(scene, camera, this)
    this.scene.add(this.cloud)

    // generate worker callback handler
    this.generateWorker.onmessage = (
      msg: MessageEvent<{
        idMap: Map<string, number>
        arrays: ArrayLike<number>[]
        blocksCount: number[]
      }>
    ) => {
      this.resetBlocks()
      this.idMap = msg.data.idMap
      this.blocksCount = msg.data.blocksCount

      for (let i = 0; i < msg.data.arrays.length; i++) {
        this.blocks[i].instanceMatrix = new THREE.InstancedBufferAttribute(
          (this.blocks[i].instanceMatrix.array = msg.data.arrays[i]),
          16
        )
      }

      for (const block of this.blocks) {
        block.instanceMatrix.needsUpdate = true
      }
    }
  }
  // core properties
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  distance = 3
  chunkSize = 24

  // terrain properties
  maxCount: number
  chunk = new THREE.Vector2(0, 0)
  previousChunk = new THREE.Vector2(0, 0)
  noise = new Noise()

  // materials
  materials = new Materials()
  materialType = [
    MaterialType.grass,
    MaterialType.sand,
    MaterialType.tree,
    MaterialType.leaf,
    MaterialType.dirt,
    MaterialType.stone,
    MaterialType.coal,
    MaterialType.wood,
    MaterialType.diamond,
    MaterialType.quartz,
    MaterialType.glass,
    MaterialType.bedrock,
    MaterialType.sandstone,
    MaterialType.cactus,
    MaterialType.jungleTree,
    MaterialType.jungleLeaf,
    MaterialType.jungleWood,
    MaterialType.water,
    MaterialType.ironOre,
    MaterialType.diamondOre,
    MaterialType.furnace,
    MaterialType.chest
  ]

  // other properties
  blocks: THREE.InstancedMesh[] = []
  blocksCount: number[] = []
  blocksFactor = [
    1, 1, 0.1, 0.7, 0.1, 0.2, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.25, 0.1,
    0.8, 4.5, 0.1, 1.5, 0.08, 0.035, 0.08, 0.05
  ]

  customBlocks: Block[] = []
  highlight: Highlight

  // water flow
  waterLevel = 28
  waterFlowMaxLevel = 6
  waterFlowGap = 250
  lastWaterFlow = 0
  waterQueue: Array<{ x: number; y: number; z: number; level: number }> = []
  waterLevels = new Map<string, number>()

  // structures already rolled, one attempt per chunk
  structuresBuilt = new Set<string>()

  idMap = new Map<string, number>()
  generateWorker = new Generate()

  // cloud
  cloud = new THREE.InstancedMesh(
    new THREE.BoxGeometry(20, 5, 14),
    new THREE.MeshStandardMaterial({
      transparent: true,
      color: 0xffffff,
      opacity: 0.4
    }),
    1000
  )
  cloudCount = 0
  cloudGap = 5

  getCount = (type: BlockType) => {
    return this.blocksCount[type]
  }

  setCount = (type: BlockType) => {
    this.blocksCount[type] = this.blocksCount[type] + 1
  }

  initBlocks = () => {
    // reset
    for (const block of this.blocks) {
      this.scene.remove(block)
    }
    this.blocks = []

    // create instance meshes
    const geometry = new THREE.BoxGeometry()

    for (let i = 0; i < this.materialType.length; i++) {
      let block = new THREE.InstancedMesh(
        geometry,
        this.materials.get(this.materialType[i]),
        this.maxCount * this.blocksFactor[i]
      )
      block.name = BlockType[i]
      this.blocks.push(block)
      this.scene.add(block)
    }

    this.blocksCount = new Array(this.materialType.length).fill(0)
  }

  resetBlocks = () => {
    // reest count and instance matrix
    for (let i = 0; i < this.blocks.length; i++) {
      this.blocks[i].instanceMatrix = new THREE.InstancedBufferAttribute(
        new Float32Array(this.maxCount * this.blocksFactor[i] * 16),
        16
      )
    }
  }

  generate = () => {
    this.blocksCount = new Array(this.blocks.length).fill(0)
    this.ensureStructures()
    // post work to generate worker
    this.generateWorker.postMessage({
      distance: this.distance,
      chunk: this.chunk,
      noiseSeed: this.noise.seed,
      treeSeed: this.noise.treeSeed,
      stoneSeed: this.noise.stoneSeed,
      coalSeed: this.noise.coalSeed,
      leafSeed: this.noise.leafSeed,
      idMap: new Map<string, number>(),
      blocksFactor: this.blocksFactor,
      blocksCount: this.blocksCount,
      customBlocks: this.customBlocks,
      chunkSize: this.chunkSize
    })

    // cloud

    if (this.cloudGap++ > 5) {
      this.cloudGap = 0
      this.cloud.instanceMatrix = new THREE.InstancedBufferAttribute(
        new Float32Array(1000 * 16),
        16
      )
      this.cloudCount = 0
      for (
        let x =
          -this.chunkSize * this.distance * 3 + this.chunkSize * this.chunk.x;
        x <
        this.chunkSize * this.distance * 3 +
          this.chunkSize +
          this.chunkSize * this.chunk.x;
        x += 20
      ) {
        for (
          let z =
            -this.chunkSize * this.distance * 3 + this.chunkSize * this.chunk.y;
          z <
          this.chunkSize * this.distance * 3 +
            this.chunkSize +
            this.chunkSize * this.chunk.y;
          z += 20
        ) {
          const matrix = new THREE.Matrix4()
          matrix.setPosition(x, 80 + (Math.random() - 0.5) * 30, z)

          if (Math.random() > 0.8) {
            this.cloud.setMatrixAt(this.cloudCount++, matrix)
          }
        }
      }
      this.cloud.instanceMatrix.needsUpdate = true
    }
  }

  // generate adjacent blocks after removing a block (vertical infinity world)
  generateAdjacentBlocks = (position: THREE.Vector3) => {
    const { x, y, z } = position
    const noise = this.noise
    const yOffset = Math.floor(
      noise.get(x / noise.gap, z / noise.gap, noise.seed) * noise.amp
    )

    if (y > 30 + yOffset) {
      return
    }

    const stoneOffset =
      noise.get(x / noise.stoneGap, z / noise.stoneGap, noise.stoneSeed) *
      noise.stoneAmp

    let type: BlockType

    if (isCaveAir(x, y, z, noise)) {
      return
    }

    if (stoneOffset > noise.stoneThreshold || y < 23) {
      type = getOreType(x, y, z, noise)
    } else {
      const biome = getBiomeType(x, z)
      if (biome === BiomeType.desert) {
        type = BlockType.sandstone
      } else if (biome === BiomeType.jungle) {
        type = BlockType.dirt
      } else {
        type = yOffset < -3 ? BlockType.sand : BlockType.dirt
      }
    }

    this.buildBlock(new THREE.Vector3(x, y - 1, z), type)
    this.buildBlock(new THREE.Vector3(x, y + 1, z), type)
    this.buildBlock(new THREE.Vector3(x - 1, y, z), type)
    this.buildBlock(new THREE.Vector3(x + 1, y, z), type)
    this.buildBlock(new THREE.Vector3(x, y, z - 1), type)
    this.buildBlock(new THREE.Vector3(x, y, z + 1), type)

    this.blocks[type].instanceMatrix.needsUpdate = true
  }

  // trunk height at (x, z), 0 means no tree; mirrors the generate worker
  getTreeHeight = (x: number, z: number) => {
    const noise = this.noise
    const yOffset = Math.floor(
      noise.get(x / noise.gap, z / noise.gap, noise.seed) * noise.amp
    )
    if (yOffset < -3) {
      return 0
    }
    const stoneOffset =
      noise.get(x / noise.stoneGap, z / noise.stoneGap, noise.stoneSeed) *
      noise.stoneAmp
    if (stoneOffset >= noise.stoneThreshold) {
      return 0
    }
    const biome = getBiomeType(x, z)
    if (biome === BiomeType.desert) {
      return 0
    }
    const treeOffset =
      noise.get(x / noise.treeGap, z / noise.treeGap, noise.treeSeed) *
      noise.treeAmp
    const treeThreshold =
      biome === BiomeType.jungle
        ? noise.treeThreshold - 1.2
        : noise.treeThreshold
    if (treeOffset <= treeThreshold) {
      return 0
    }
    return biome === BiomeType.jungle ? noise.treeHeight + 4 : noise.treeHeight
  }

  // ocean / pond checks mirrored from the generate worker
  isOcean = (x: number, z: number) => {
    const noise = this.noise
    return (
      noise.get(x / 80, z / 80, noise.seed * 1.9) < -0.32 &&
      noise.get(x / 24, z / 24, noise.treeSeed * 2.1) < 0.42
    )
  }

  isPond = (x: number, z: number) => {
    const noise = this.noise
    return noise.get((x + 120) / 18, (z - 80) / 18, noise.seed * 3.4) > 0.48
  }

  // surface offset with the worker's ocean adjustment
  getAdjustedYOffset = (x: number, z: number) => {
    const noise = this.noise
    let yOffset = Math.floor(
      noise.get(x / noise.gap, z / noise.gap, noise.seed) * noise.amp
    )
    if (this.isOcean(x, z)) {
      const islandLift = Math.max(
        0,
        noise.get((x - 12) / 18, (z + 12) / 18, noise.treeSeed * 2.5)
      )
      yOffset = Math.min(yOffset - 7, -5 + Math.floor(islandLift * 9))
    }
    return yOffset
  }

  isNaturalWater = (x: number, y: number, z: number) => {
    const yOffset = this.getAdjustedYOffset(x, z)
    const surfaceY = 30 + yOffset
    if (y <= surfaceY || y > this.waterLevel || surfaceY >= this.waterLevel) {
      return false
    }
    return this.isOcean(x, z) || this.isPond(x, z) || yOffset < -5
  }

  // air means water can flow in
  isAirCell = (x: number, y: number, z: number) => {
    let removed = false
    for (const block of this.customBlocks) {
      if (block.x === x && block.y === y && block.z === z) {
        if (block.placed) {
          return false
        }
        removed = true
      }
    }
    if (removed) {
      return true
    }
    const yOffset = this.getAdjustedYOffset(x, z)
    if (y <= 30 + yOffset) {
      return false
    }
    if (this.isNaturalWater(x, y, z)) {
      return false
    }
    const treeHeight = this.getTreeHeight(x, z)
    if (treeHeight > 0 && y <= 30 + yOffset + treeHeight) {
      return false
    }
    return true
  }

  isWaterCell = (x: number, y: number, z: number) => {
    let removed = false
    for (const block of this.customBlocks) {
      if (block.x === x && block.y === y && block.z === z) {
        if (block.placed) {
          return block.type === BlockType.water
        }
        removed = true
      }
    }
    if (removed) {
      return false
    }
    return this.isNaturalWater(x, y, z)
  }

  // seed flow from water cells next to a changed block
  triggerWaterFlow = (position: THREE.Vector3) => {
    for (const [dx, dy, dz] of [
      [0, -1, 0],
      [0, 1, 0],
      [1, 0, 0],
      [-1, 0, 0],
      [0, 0, 1],
      [0, 0, -1]
    ]) {
      const x = position.x + dx
      const y = position.y + dy
      const z = position.z + dz
      if (this.isWaterCell(x, y, z)) {
        this.waterQueue.push({
          x,
          y,
          z,
          level: this.waterLevels.get(`${x}_${y}_${z}`) ?? 0
        })
      }
    }
  }

  placeFlowWater = (x: number, y: number, z: number, level: number) => {
    const matrix = new THREE.Matrix4()
    matrix.setPosition(x, y, z)
    this.blocks[BlockType.water].setMatrixAt(
      this.getCount(BlockType.water),
      matrix
    )
    this.setCount(BlockType.water)
    this.blocks[BlockType.water].instanceMatrix.needsUpdate = true
    this.customBlocks.push(new Block(x, y, z, BlockType.water, true))
    this.waterLevels.set(`${x}_${y}_${z}`, level)
  }

  // spread one wave of water per waterFlowGap ms
  updateWaterFlow = () => {
    if (this.waterQueue.length === 0) {
      return
    }
    const now = performance.now()
    if (now - this.lastWaterFlow < this.waterFlowGap) {
      return
    }
    this.lastWaterFlow = now

    const capacity = Math.floor(
      this.maxCount * this.blocksFactor[BlockType.water]
    )
    const frontier = this.waterQueue
    this.waterQueue = []

    for (const source of frontier) {
      const targets: Array<{
        x: number
        y: number
        z: number
        level: number
      }> = []

      // fall first
      if (source.y - 1 > 0 && this.isAirCell(source.x, source.y - 1, source.z)) {
        targets.push({
          x: source.x,
          y: source.y - 1,
          z: source.z,
          level: source.level
        })
      } else if (source.level < this.waterFlowMaxLevel) {
        // spread sideways once settled
        for (const [dx, dz] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1]
        ]) {
          const x = source.x + dx
          const z = source.z + dz
          if (this.isAirCell(x, source.y, z)) {
            targets.push({ x, y: source.y, z, level: source.level + 1 })
          }
        }
      }

      for (const target of targets) {
        if (this.getCount(BlockType.water) >= capacity) {
          return
        }
        this.placeFlowWater(target.x, target.y, target.z, target.level)
        this.waterQueue.push(target)
      }
    }
  }

  // deterministic structure rng per chunk
  structureRand = (cx: number, cz: number, salt: number) => {
    const value =
      Math.sin(cx * 127.1 + cz * 311.7 + salt * 269.5 + this.noise.seed * 74.7) *
      43758.5453
    return value - Math.floor(value)
  }

  stampPlace = (x: number, y: number, z: number, type: BlockType) => {
    this.customBlocks.push(new Block(x, y, z, type, true))
  }

  // carve the natural surface block with its real type
  stampCarve = (x: number, y: number, z: number) => {
    const noise = this.noise
    const yOffset = Math.floor(
      noise.get(x / noise.gap, z / noise.gap, noise.seed) * noise.amp
    )
    if (y !== 30 + yOffset) {
      return
    }
    const stoneOffset =
      noise.get(x / noise.stoneGap, z / noise.stoneGap, noise.stoneSeed) *
      noise.stoneAmp
    const coalOffset =
      noise.get(x / noise.coalGap, z / noise.coalGap, noise.coalSeed) *
      noise.coalAmp
    this.customBlocks.push(
      new Block(
        x,
        y,
        z,
        getSurfaceBlockType(x, z, yOffset, stoneOffset, coalOffset, noise),
        false
      )
    )
  }

  // roll structures for chunks in the current generate range, once per chunk
  ensureStructures = () => {
    for (
      let cx = this.chunk.x - this.distance;
      cx <= this.chunk.x + this.distance;
      cx++
    ) {
      for (
        let cz = this.chunk.y - this.distance;
        cz <= this.chunk.y + this.distance;
        cz++
      ) {
        const key = `${cx}_${cz}`
        if (this.structuresBuilt.has(key)) {
          continue
        }
        this.structuresBuilt.add(key)

        if (this.structureRand(cx, cz, 0) > 0.05) {
          continue
        }

        const x0 =
          cx * this.chunkSize +
          4 +
          Math.floor(this.structureRand(cx, cz, 1) * 14)
        const z0 =
          cz * this.chunkSize +
          4 +
          Math.floor(this.structureRand(cx, cz, 2) * 14)
        const type = Math.floor(this.structureRand(cx, cz, 3) * 3)

        this.buildStructure(type, x0, z0, cx, cz)
      }
    }
  }

  buildStructure = (
    type: number,
    x0: number,
    z0: number,
    cx: number,
    cz: number
  ) => {
    const size = type === 0 ? 7 : 5
    let salt = 10
    const rand = () => this.structureRand(cx, cz, salt++)

    // skip trees
    for (let x = x0 - 2; x < x0 + size + 2; x++) {
      for (let z = z0 - 2; z < z0 + size + 2; z++) {
        if (this.getTreeHeight(x, z) > 0) {
          return
        }
      }
    }
    // skip water and steep ground
    const surfaceY = 30 + this.getAdjustedYOffset(x0 + 2, z0 + 2)
    for (let x = x0; x < x0 + size; x++) {
      for (let z = z0; z < z0 + size; z++) {
        const y = 30 + this.getAdjustedYOffset(x, z)
        if (y < this.waterLevel || Math.abs(y - surfaceY) > 1) {
          return
        }
      }
    }
    // skip existing builds
    for (const block of this.customBlocks) {
      if (
        block.x >= x0 - 1 &&
        block.x <= x0 + size &&
        block.z >= z0 - 1 &&
        block.z <= z0 + size &&
        block.y >= surfaceY - 1 &&
        block.y <= surfaceY + 7
      ) {
        return
      }
    }

    const desert = getBiomeType(x0, z0) === BiomeType.desert
    const jungle = getBiomeType(x0, z0) === BiomeType.jungle
    const floorType = desert
      ? BlockType.sandstone
      : jungle
        ? BlockType.jungleWood
        : BlockType.wood
    const logType = desert
      ? BlockType.sandstone
      : jungle
        ? BlockType.jungleTree
        : BlockType.tree

    if (type === 0) {
      this.buildHouse(x0, surfaceY, z0, floorType, logType)
    } else if (type === 1) {
      this.buildWell(x0, surfaceY, z0, floorType, logType)
    } else {
      this.buildRuin(x0, surfaceY, z0, rand)
    }
  }

  buildHouse = (
    x0: number,
    surfaceY: number,
    z0: number,
    floorType: BlockType,
    logType: BlockType
  ) => {
    const size = 7
    // floor replaces the surface
    for (let x = x0; x < x0 + size; x++) {
      for (let z = z0; z < z0 + size; z++) {
        this.stampCarve(x, surfaceY, z)
        this.stampPlace(x, surfaceY, z, floorType)
      }
    }
    // walls with log corners, two windows and a door
    for (let x = x0; x < x0 + size; x++) {
      for (let z = z0; z < z0 + size; z++) {
        const edge =
          x === x0 || x === x0 + size - 1 || z === z0 || z === z0 + size - 1
        if (!edge) {
          continue
        }
        const corner =
          (x === x0 || x === x0 + size - 1) &&
          (z === z0 || z === z0 + size - 1)
        for (let dy = 1; dy <= 3; dy++) {
          // door gap on the south wall
          if (z === z0 + size - 1 && x === x0 + 3 && dy <= 2) {
            continue
          }
          if (corner) {
            this.stampPlace(x, surfaceY + dy, z, logType)
          } else if (
            (x === x0 || x === x0 + size - 1) &&
            z === z0 + 3 &&
            dy === 2
          ) {
            this.stampPlace(x, surfaceY + dy, z, BlockType.glass)
          } else {
            this.stampPlace(x, surfaceY + dy, z, floorType)
          }
        }
      }
    }
    // roof
    for (let x = x0; x < x0 + size; x++) {
      for (let z = z0; z < z0 + size; z++) {
        this.stampPlace(x, surfaceY + 4, z, floorType)
      }
    }
    // a chest inside
    this.stampPlace(x0 + 1, surfaceY + 1, z0 + 1, BlockType.chest)
  }

  buildWell = (
    x0: number,
    surfaceY: number,
    z0: number,
    floorType: BlockType,
    logType: BlockType
  ) => {
    // carved water hole in the center
    const cx = x0 + 2
    const cz = z0 + 2
    this.stampCarve(cx, surfaceY, cz)
    this.stampPlace(cx, surfaceY, cz, BlockType.water)
    // sandstone rim
    for (let x = x0 + 1; x <= x0 + 3; x++) {
      for (let z = z0 + 1; z <= z0 + 3; z++) {
        if (x === cx && z === cz) {
          continue
        }
        this.stampPlace(x, surfaceY + 1, z, BlockType.sandstone)
      }
    }
    // corner posts and a small roof
    for (const [px, pz] of [
      [x0 + 1, z0 + 1],
      [x0 + 3, z0 + 1],
      [x0 + 1, z0 + 3],
      [x0 + 3, z0 + 3]
    ]) {
      this.stampPlace(px, surfaceY + 2, pz, logType)
      this.stampPlace(px, surfaceY + 3, pz, logType)
    }
    for (let x = x0; x < x0 + 5; x++) {
      for (let z = z0; z < z0 + 5; z++) {
        this.stampPlace(x, surfaceY + 4, z, floorType)
      }
    }
  }

  buildRuin = (
    x0: number,
    surfaceY: number,
    z0: number,
    rand: () => number
  ) => {
    const size = 5
    // broken stone walls
    for (let x = x0; x < x0 + size; x++) {
      for (let z = z0; z < z0 + size; z++) {
        const edge =
          x === x0 || x === x0 + size - 1 || z === z0 || z === z0 + size - 1
        if (!edge) {
          continue
        }
        const height = 2 + Math.floor(rand() * 4)
        for (let dy = 1; dy <= height; dy++) {
          if (rand() < 0.18) {
            continue
          }
          this.stampPlace(x, surfaceY + dy, z, BlockType.stone)
        }
      }
    }
    // rubble
    const rubble = 2 + Math.floor(rand() * 3)
    for (let i = 0; i < rubble; i++) {
      const x = x0 + 1 + Math.floor(rand() * (size - 2))
      const z = z0 + 1 + Math.floor(rand() * (size - 2))
      this.stampPlace(x, surfaceY + 1, z, BlockType.stone)
    }
  }

  buildBlock = (position: THREE.Vector3, type: BlockType) => {
    const noise = this.noise
    // check if it's natural terrain
    const yOffset = Math.floor(
      noise.get(position.x / noise.gap, position.z / noise.gap, noise.seed) *
        noise.amp
    )
    if (position.y >= 30 + yOffset || position.y < 0) {
      return
    }

    position.y === 0 && (type = BlockType.bedrock)

    // check custom blocks
    for (const block of this.customBlocks) {
      if (
        block.x === position.x &&
        block.y === position.y &&
        block.z === position.z
      ) {
        return
      }
    }

    // instance buffer is full
    if (
      this.getCount(type) >=
      Math.floor(this.maxCount * this.blocksFactor[type])
    ) {
      return
    }

    // build block
    this.customBlocks.push(
      new Block(position.x, position.y, position.z, type, true)
    )

    const matrix = new THREE.Matrix4()
    matrix.setPosition(position)
    this.blocks[type].setMatrixAt(this.getCount(type), matrix)
    this.blocks[type].instanceMatrix.needsUpdate = true
    this.setCount(type)
  }

  update = () => {
    this.chunk.set(
      Math.floor(this.camera.position.x / this.chunkSize),
      Math.floor(this.camera.position.z / this.chunkSize)
    )

    //generate terrain when getting into new chunk
    if (
      this.chunk.x !== this.previousChunk.x ||
      this.chunk.y !== this.previousChunk.y
    ) {
      this.generate()
    }

    this.previousChunk.copy(this.chunk)

    this.highlight.update()
    this.updateWaterFlow()
  }
}
